import { supabase } from '@/lib/supabase';

/**
 * MEMBERSHIP COUPONS — single client-side source of truth.
 *
 * Validation always runs on the server (membership-coupons -> mkt_validate_coupon),
 * and checkout-link re-validates before building the hosted checkout URL, so nothing
 * here can grant a discount by itself. The card is charged by GoHighLevel; credits are
 * always the plan's full allowance whatever was paid.
 *
 * Shareable link for a customer:  https://giggone.com/en/pricing?plan=pro&coupon=PRO10
 */

export const COUPON_PLANS = ['basic', 'pro', 'premium'] as const;
export type CouponPlan = (typeof COUPON_PLANS)[number];

export interface CouponQuote {
  valid: boolean;
  reason?: string;
  code?: string;
  name?: string;
  plan?: string;
  discountType?: 'amount' | 'percent';
  amountOffCents?: number | null;
  percentOff?: number | null;
  listPriceCents?: number;
  discountCents?: number;
  finalPriceCents?: number;
  duration?: 'once' | 'repeating' | 'forever';
  durationMonths?: number | null;
  expiresAt?: string | null;
  eligiblePlans?: string[];
  /** Intro offers only: credits granted for the discounted first month (e.g. 3). */
  introCredits?: number | null;
}

const STORAGE_KEY = 'gg_coupon_code';

export const normalizeCouponCode = (v: string) => String(v || '').toUpperCase().trim().replace(/[^A-Z0-9_-]/g, '');

/** Keep a code from a shared link across the login / register round-trip. */
export const rememberCoupon = (code: string) => {
  try {
    const c = normalizeCouponCode(code);
    if (c) sessionStorage.setItem(STORAGE_KEY, c);
  } catch (_e) { /* private mode */ }
};
export const readRememberedCoupon = (): string => {
  try { return sessionStorage.getItem(STORAGE_KEY) || ''; } catch (_e) { return ''; }
};
export const forgetCoupon = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
};

export const validateCoupon = async (code: string, plan: string, userId?: string | null): Promise<CouponQuote> => {
  const c = normalizeCouponCode(code);
  if (!c) return { valid: false, reason: 'empty' };
  const { data, error } = await supabase.functions.invoke('membership-coupons', {
    body: { action: 'validate', code: c, plan, userId: userId || undefined },
  });
  if (error || !data) return { valid: false, reason: 'error' };
  return data as CouponQuote;
};

const REASONS: Record<string, { en: string; es: string }> = {
  empty: { en: 'Enter a code.', es: 'Ingrese un código.' },
  not_found: { en: 'That code does not exist. Check the spelling.', es: 'Ese código no existe. Revise la ortografía.' },
  inactive: { en: 'That code is no longer active.', es: 'Ese código ya no está activo.' },
  not_started: { en: 'That code is not active yet.', es: 'Ese código todavía no está activo.' },
  expired: { en: 'That code has expired.', es: 'Ese código ha vencido.' },
  plan_not_eligible: { en: 'That code does not apply to this plan.', es: 'Ese código no aplica a este plan.' },
  fully_redeemed: { en: 'That code has reached its redemption limit.', es: 'Ese código alcanzó su límite de uso.' },
  already_used: { en: 'You have already used this code.', es: 'Ya usó este código.' },
  intro_new_members_only: { en: 'This intro offer is for new members only.', es: 'Esta oferta de introducción es solo para miembros nuevos.' },
  // 2026-10-13 Square migration: coupons are held until they are recreated on the new Square-backed GHL product.
  coupon_paused_processor_switch: { en: 'Promo codes are briefly unavailable while we switch card processors. You can still check out at the regular price.', es: 'Los códigos promocionales no están disponibles por un momento mientras cambiamos de procesador de tarjetas. Puede pagar al precio regular.' },
  error: { en: 'Could not check the code right now. Try again.', es: 'No se pudo verificar el código. Intente de nuevo.' },
};

export const couponReasonText = (reason: string | undefined, lang: string) => {
  const r = REASONS[reason || 'error'] || REASONS.error;
  return lang === 'es' ? r.es : r.en;
};

/** "First month only", "First 3 months", "Every month". */
export const couponDurationText = (q: Pick<CouponQuote, 'duration' | 'durationMonths'>, lang: string) => {
  if (q.duration === 'forever') return lang === 'es' ? 'todos los meses' : 'every month';
  if (q.duration === 'repeating') {
    const n = q.durationMonths || 1;
    return lang === 'es' ? `los primeros ${n} meses` : `first ${n} month${n === 1 ? '' : 's'}`;
  }
  return lang === 'es' ? 'solo el primer mes' : 'first month only';
};

export const couponDiscountLabel = (c: { discount_type?: string; discountType?: string; amount_off_cents?: number | null; amountOffCents?: number | null; percent_off?: number | null; percentOff?: number | null }) => {
  const type = c.discount_type || c.discountType;
  if (type === 'percent') return `${c.percent_off ?? c.percentOff}% off`;
  const cents = Number(c.amount_off_cents ?? c.amountOffCents ?? 0);
  return `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)} off`;
};
